import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { toast } from "react-toastify";
import { setGeneratedStops, nextStep, previousStep } from "../../../redux/routeFLowSlice";
import { handleGetRequest } from "../../../services/GetTemplate";
import styles from "./CreateStopsStep.module.scss";

function CreateStopsStep({ deliveryId }) {
    const dispatch = useDispatch();
    const scannedPackages = useSelector((state) => state.routeFlowSlice.scannedPackages);
    const generatedStops = useSelector((state) => state.routeFlowSlice.generatedStops);
    const [loading, setLoading] = useState(false);
    const [packagesHash, setPackagesHash] = useState("");

    // Generate a hash of package IDs to detect changes
    useEffect(() => {
        const currentHash = scannedPackages
            .map((pkg) => pkg.Waybill)
            .sort()
            .join(",");

        // If packages changed or no stops exist, regenerate
        if (currentHash !== packagesHash || generatedStops.length === 0) {
            setPackagesHash(currentHash);
            generateStops();
        }
    }, [scannedPackages]); // Re-run when scannedPackages change

    const generateStops = async () => {
        if (scannedPackages.length === 0) {
            toast.warning("No packages to generate stops from");
            return;
        }

        try {
            setLoading(true);

            // Group packages by clientId
            const packagesByClient = {};

            for (const pkg of scannedPackages) {
                const clientId = pkg.name?._id || pkg.name;

                if (!packagesByClient[clientId]) {
                    packagesByClient[clientId] = {
                        clientId,
                        packages: [],
                        clientInfo: null,
                    };
                }

                packagesByClient[clientId].packages.push(pkg);
            }

            // Fetch client details and create stops
            const stops = [];
            let stopNumber = 1;

            for (const clientId in packagesByClient) {
                const clientData = packagesByClient[clientId];

                // Fetch client details to get coordinates
                const userResponse = await handleGetRequest(`/getuser/${clientId}`);

                if (userResponse?.success && userResponse?.data) {
                    const user = userResponse.data;

                    const stop = {
                        stopId: `STOP-${clientId}-${Date.now()}`,
                        stopNumber,
                        clientId,
                        clientName: `${user.firstName} ${user.lastName}`,
                        latitude: user.latitude || 0,
                        longitude: user.longitude || 0,
                        status: "Not Delivered",
                        packages: clientData.packages,
                        address: user.client?.deliveryAddress || user.courier?.deliveryAddress || "N/A",
                        email: user.email || "",
                        phone: user.phone || "",
                    };

                    stops.push(stop);
                    stopNumber++;
                }
            }

            // Sort stops for consistency
            stops.sort((a, b) => a.clientName.localeCompare(b.clientName));

            // Reassign stop numbers after sorting
            const numberedStops = stops.map((stop, index) => ({
                ...stop,
                stopNumber: index + 1,
            }));

            dispatch(setGeneratedStops(numberedStops));
            toast.success(`Generated ${numberedStops.length} stop(s) from ${scannedPackages.length} package(s)`);
        } catch (error) {
            console.error("Error generating stops:", error);
            toast.error("Failed to generate stops. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        if (generatedStops.length === 0) {
            toast.error("No stops available to proceed");
            return;
        }
        dispatch(nextStep());
    };

    const handleBack = () => {
        dispatch(previousStep());
    };

    const handleRegenerateManually = () => {
        generateStops();
    };

    return (
        <div className={styles.createStopsContainer}>
            <div className={styles.header}>
                <h2>Generated Stops</h2>
                <p>Packages grouped by client location</p>
            </div>

            {/* Regenerate Button */}
            <div className={styles.controlBar}>
                <Button label="Regenerate Stops" icon="pi pi-refresh" onClick={handleRegenerateManually} className="p-button-outlined" loading={loading} />
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "3rem" }}></i>
                    <p>Generating stops...</p>
                </div>
            ) : generatedStops.length === 0 ? (
                <div className={styles.emptyState}>
                    <i className="pi pi-map-marker" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                    <p>No stops generated</p>
                    <Button label="Generate Stops" icon="pi pi-refresh" onClick={generateStops} />
                </div>
            ) : (
                <div className={styles.stopsGrid}>
                    {generatedStops.map((stop) => (
                        <Card key={stop.stopId} className={styles.stopCard}>
                            <div className={styles.stopHeader}>
                                <div className={styles.stopNumber}>
                                    <span>Stop #{stop.stopNumber}</span>
                                </div>
                                <Chip label={stop.status} className={styles.statusChip} />
                            </div>

                            <div className={styles.stopBody}>
                                <div className={styles.clientInfo}>
                                    <i className="pi pi-user"></i>
                                    <span className={styles.clientName}>{stop.clientName}</span>
                                </div>

                                <div className={styles.addressInfo}>
                                    <i className="pi pi-map-marker"></i>
                                    <span>{stop.address}</span>
                                </div>

                                {stop.phone && (
                                    <div className={styles.phoneInfo}>
                                        <i className="pi pi-phone"></i>
                                        <span>{stop.phone}</span>
                                    </div>
                                )}

                                <div className={styles.coordinates}>
                                    <small>
                                        📍 {stop.latitude.toFixed(6)}, {stop.longitude.toFixed(6)}
                                    </small>
                                </div>

                                <div className={styles.packageCount}>
                                    <i className="pi pi-box"></i>
                                    <span>{stop.packages.length} package(s)</span>
                                </div>

                                {/* Package List */}
                                <div className={styles.packageList}>
                                    {stop.packages.map((pkg, idx) => (
                                        <div key={idx} className={styles.packageItem}>
                                            <span className={styles.waybill}>{pkg.Waybill}</span>
                                            <span className={styles.description}>{pkg.description || "No description"}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Summary */}
            {generatedStops.length > 0 && (
                <div className={styles.summary}>
                    <div className={styles.summaryItem}>
                        <i className="pi pi-map-marker"></i>
                        <span>Total Stops: {generatedStops.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <i className="pi pi-box"></i>
                        <span>Total Packages: {scannedPackages.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <i className="pi pi-users"></i>
                        <span>Unique Clients: {generatedStops.length}</span>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <div className={styles.navigation}>
                <Button label="Back" icon="pi pi-arrow-left" onClick={handleBack} className="p-button-outlined" />
                <Button label="Next: Split Stops" icon="pi pi-arrow-right" iconPos="right" onClick={handleNext} disabled={generatedStops.length === 0} />
            </div>
        </div>
    );
}

export default CreateStopsStep;
